import { useToast } from '@ui/toasts/useToastManager'
import { Order, OrderConfigResponse } from '@ca/common'
import useOrderPayload from '@order/hooks/useOrderPayload'
import { useOrderState } from '@order/hooks/useOrderState'
import { debugOrder } from '@order/utils/getDebugLog'
import { OrderCompare } from '@order/utils/orderCompare'
import { createOrderSubmitGuard } from '@order/utils/orderSubmitGuard'
import preserveFinalPrices from '@order/utils/preserveFinalPrices'
import { TRPCClientErrorBase } from '@trpc/client'
import { isTRPCClientError, RouterInputs, trpc } from 'api'
import Analytics from 'hooks/analytics/Analytics'
import { useIsFocused } from '@ui/hooks/useIsFocused'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'
import { useDealByBundleType } from './useDeal'
import useStockValid from './useStockValid'
import useUpdateDiscounts from './useUpdateDiscounts'
import { useOrderConfigureState } from './useOrderConfigureState'

const getErpResponse = (result: OrderConfigResponse, localId: string) => {
  const order = result.orders.find((o) => o.data.localId === localId)
  if (!order) {
    return null
  }
  const { data, ...res } = order
  return res
}

type OrderUpdate = RouterInputs['order']['patch']['orders'][number] & {
  changed: boolean
}

// `updated` writes to the ERP, `changed` only asks for a fresh response
const getOrderUpdates = (
  incoming: Order[],
  result: OrderConfigResponse,
  isGlobalDiff: boolean
): OrderUpdate[] => {
  const current = result.orders.map((order) => order.data)
  return incoming.map((order) => {
    const stored = current.find((c) => c.localId === order.localId)
    if (!stored) {
      return {
        updated: true,
        changed: true,
        res: getErpResponse(result, order.localId),
        data: order,
      }
    }
    const isSameErp = OrderCompare.isSameErpOrder(order, stored)
    if (!isSameErp) {
      debugOrder.log('erp payload changed', {
        localId: order.localId,
        paths: OrderCompare.getChangedPaths(order, stored),
      })
    }
    return {
      updated: !isSameErp,
      // A new invoice id cannot be patched into the ERP, but the result needs it
      changed:
        isGlobalDiff || !isSameErp || !OrderCompare.isSameOrder(order, stored),
      res: getErpResponse(result, order.localId),
      data: order,
    }
  })
}

const useOrderConfigure = (next: boolean) => {
  const { payload, error } = useOrderPayload()
  const { t } = useTranslation(['toasts'])
  const focused = useIsFocused()
  const [toggleManual, setToggleManual] = useState(false)
  const toast = useToast()
  const setResult = useOrderState((s) => s.setResult)
  const result = useOrderState((s) => s.result)
  const isStockValid = useStockValid()
  const deals = useDealByBundleType()
  const items = useOrderState((s) => s.order.items)

  const [isError, setIsError] = useState(false)

  const { isPending, setPending, setSubmitError } = useOrderConfigureState()

  const [submitGuard] = useState(() => createOrderSubmitGuard())

  const handleError = (err: TRPCClientErrorBase<any>) => {
    if (isTRPCClientError(error) && error.data?.code === 'TOO_MANY_REQUESTS') {
      toast({
        title: t('toasts:warning.order.configure.title'),
        text: err.message,
        variant: 'warning',
      })
      console.warn(err)
      return
    }
    setIsError(true)
    toast({
      title: t('toasts:error.order.configure.title'),
      text: err.message,
      variant: 'error',
    })
    console.error(err)
  }

  const configureOrder = trpc.order.configure.useMutation({
    onSuccess: (res, req) => {
      setResult(res)
      setIsError(false)
      setPending(false)
      setSubmitError(null)
      const pdType = useOrderState.getState().order.pd.current
      res.orders.forEach((order) => {
        const counts: Record<string, number> = {}
        const item = items.find((i) => i.id === order.data.localId)
        const bundle = item?.bundleType || 'base'
        counts[bundle] = (counts[bundle] || 0) + 1
        Analytics.logEvent('order_created', {
          orderId: `${order.id}`,
          count: `${counts[bundle]}`,
          bundle: `${bundle}`,
          frame: `${order.data.frame}`,
          color: `${order.data.color}`,
          tinting: `${order.data.tinting}`,
          strategy_ref: `${order.data.strategy_ref}`,
          lens: `${order.data.lens}`,
          frame_from_stock: `${order.data.frame_from_stock}`,
          pd_type: pdType || 'none',
        })
      })
    },
    retry: false,
    onError: (err) => {
      setPending(false)
      setSubmitError(err.message)
      handleError(err)
    },
  })

  const discounts = useOrderState((state) => state.discounts)

  const updateDiscounts = useUpdateDiscounts()

  const handlePatchResult = (
    incoming: OrderConfigResponse,
    req: RouterInputs['order']['patch']
  ) => {
    setIsError(false)

    const patchedIds = new Set(
      incoming.orders
        .map((o) => o.id)
        .filter((id) => {
          const reqOrder = req.orders.find((o) => o.res?.id === id)
          return reqOrder?.updated
        })
    )
    // A refresh patch returns the gross ERP amount, so discounted prices must survive it
    const result = preserveFinalPrices(
      incoming,
      useOrderState.getState().result,
      patchedIds
    )

    if (!discounts?.length) {
      setResult(result)
      return
    }
    const discountedIds = new Set<number>()
    discounts.forEach((discount) => {
      discount.input.orders.forEach((order) => {
        discountedIds.add(order.id)
      })
    })

    const wasPatched = [...patchedIds].some((id) => discountedIds.has(id))
    debugOrder.log('handlePatchResult', {
      discounts,
      wasPatched,
    })
    setResult(result)
    if (wasPatched) {
      updateDiscounts.update(discounts)
    }
  }

  const patchOrder = trpc.order.patch.useMutation({
    onSuccess: (res, req) => {
      setPending(false)
      setSubmitError(null)
      submitGuard.recordSuccess(req, res)
      // Track patched orders
      res.orders.forEach((order) => {
        const reqOrder = req.orders.find((o) => o.res?.id === order.id)
        if (!reqOrder?.updated) {
          return
        }
        const item = items.find((i) => i.id === order.data.localId)
        const bundle = item?.bundleType || 'base'
        Analytics.logEvent('order_patched', {
          orderId: `${order.id}`,
          bundle: `${bundle}`,
          frame: `${order.data.frame}`,
          color: `${order.data.color}`,
          tinting: `${order.data.tinting}`,
          strategy_ref: `${order.data.strategy_ref}`,
          lens: `${order.data.lens}`,
          frame_from_stock: `${order.data.frame_from_stock}`,
        })
      })

      // Handle patch result
      handlePatchResult(res, req)
    },
    onError: (err) => {
      setPending(false)
      setSubmitError(err.message)
      handleError(err)
    },
  })

  const orderCreate = useDebouncedCallback(
    (data: RouterInputs['order']['configure']) => {
      if (!focused || isPending) {
        return
      }
      setPending(true)
      return configureOrder.mutate(data)
    },
    200,
    {
      leading: false,
    }
  )

  const orderPatch = useDebouncedCallback(
    (data: RouterInputs['order']['patch']) => {
      if (!focused || isPending) {
        return
      }
      setPending(true)
      return patchOrder.mutate(data)
    },
    200,
    {
      leading: false,
    }
  )

  const manualSubmit = useCallback(() => {
    if (next) {
      return
    }
    if (error) {
      toast({
        title: t('toasts:error.order.configure.title'),
        text: error || t('toasts:error.default.desc'),
        variant: 'error',
      })
      return
    }
    setIsError(false)
    submitGuard.reset()
    setToggleManual((prev) => !prev)
  }, [error, next, toast, t, submitGuard])

  const totalFrames = (deals.base?.frames || 0) + (deals.extra?.frames || 0)
  useEffect(() => {
    if (
      !focused ||
      !payload ||
      next ||
      isPending ||
      isError ||
      payload?.orders.length < totalFrames
    ) {
      return
    }

    if (!result) {
      orderCreate(payload)
      return
    }

    const orders = result.orders.map((order) => order.data)

    const isGlobalDiff = payload.invoice_id !== result.invoice_id
    const updates = getOrderUpdates(payload.orders, result, isGlobalDiff)

    debugOrder.log({ updates, payloadOrders: payload.orders, orders })
    if (result.isValid && updates.every((update) => !update.changed)) {
      debugOrder.log('Skipping submit no changes found', {
        payload: payload.orders,
        result: orders,
        isValid: result.isValid,
      })

      return
    }

    const data = {
      ...payload,
      orders: updates.map((update) => ({
        updated: update.updated,
        res: update.res,
        data: update.data,
      })),
    }

    if (
      submitGuard.shouldSuppress(data, payload.orders, orders, result.isValid)
    ) {
      return
    }

    // We want to see all order submits
    debugOrder.log('Submitting order', { data })

    orderPatch(data)
  }, [
    payload,
    next,
    focused,
    deals,
    isStockValid,
    totalFrames,
    toggleManual,
    result?.isValid,
    isError,
    submitGuard,
  ])

  return {
    manualSubmit,
    isLoading: isPending || updateDiscounts.isLoading,
    payloadError: error,
    isError,
  }
}

export default useOrderConfigure
